import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { X, Sparkles } from 'lucide-react';

const DISMISS_KEY = 'scroll_cta_dismissed_v1';
const SCROLL_TRIGGER_PCT = 0.6;
const RAW_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BACKEND_URL = (!RAW_BACKEND_URL || RAW_BACKEND_URL === 'undefined') ? '' : RAW_BACKEND_URL;

/**
 * Floating scroll-triggered CTA banner.
 * Slides in from bottom-right after the user has scrolled past 60% of the page.
 * Dismissal persists in sessionStorage so returning visitors in the same tab
 * aren't re-pestered.
 */
export const ScrollCtaBanner = () => {
  const [visible, setVisible] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
  });

  // Platform owner can turn this banner off from Admin → Settings.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${BACKEND_URL}/api/public/site-config`)
      .then((r) => { if (!cancelled) setEnabled(r.data?.scroll_cta_banner_enabled !== false); })
      .catch(() => { /* default: show */ });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (dismissed) return;
    const onScroll = () => {
      const h = document.documentElement;
      const scrolled = (h.scrollTop + window.innerHeight) / h.scrollHeight;
      if (scrolled >= SCROLL_TRIGGER_PCT) setVisible(true);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [dismissed]);

  const handleDismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* noop */ }
    setDismissed(true);
    setVisible(false);
  };

  if (dismissed || !visible || !enabled) return null;

  return (
    <div
      className="fixed bottom-4 right-4 left-4 sm:left-auto z-40 max-w-sm animate-in slide-in-from-bottom-5 duration-500"
      data-testid="scroll-cta-banner"
    >
      <div className="bg-zinc-900 text-white rounded-xl shadow-2xl border border-zinc-800 p-4 flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-indigo-600 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold leading-tight">Start free — no credit card.</p>
          <p className="text-xs text-zinc-400 mt-0.5 mb-2.5">5 posts + 5 AI images / month, free forever. Upgrade anytime.</p>
          <Link
            to="/pricing"
            onClick={handleDismiss}
            className="inline-flex items-center gap-1 text-sm font-semibold text-indigo-300 hover:text-white transition-colors"
            data-testid="scroll-cta-register"
          >
            Get started — choose a plan →
          </Link>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          className="w-6 h-6 rounded-md text-zinc-500 hover:text-white hover:bg-zinc-800 flex items-center justify-center flex-shrink-0 transition-colors"
          aria-label="Dismiss"
          data-testid="scroll-cta-dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

export default ScrollCtaBanner;
